import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HeroSection from '../components/HeroSection';
import CourseCard from '../components/CourseCard';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ErrorMessage from '../components/ui/ErrorMessage';
import courseService from '../services/courseService';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch courses from API on mount
  useEffect(() => {
    fetchCourses();
  }, []);

  /**
   * Fetch all courses from the backend
   */
  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await courseService.getAllCourses();
      setCourses(data);
      setFilteredCourses(data);
    } catch (err) {
      console.error('Failed to fetch courses:', err);
      setError(err.response?.data?.detail || 'Failed to load courses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter courses when search term changes
  useEffect(() => {
    if (!courses.length) return;

    if (searchTerm.trim() === '') {
      setFilteredCourses(courses);
    } else {
      const filtered = courses.filter(course =>
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCourses(filtered);
    }
  }, [searchTerm, courses]);

  const handleSearch = (query) => {
    setSearchTerm(query);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <HeroSection onSearch={handleSearch} />

      {/* Courses Section */}
      <section id="courses-section" className="py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Available Courses</h2>

          {/* Loading State */}
          {loading && <LoadingSpinner message="Loading courses..." />}

          {/* Error State */}
          {error && !loading && <ErrorMessage message={error} onRetry={fetchCourses} />}

          {/* Courses Grid */}
          {!loading && !error && (
            <>
              {searchTerm && (
                <p className="text-center text-slate-600 mb-4">
                  {filteredCourses.length > 0
                    ? `Found ${filteredCourses.length} course${filteredCourses.length !== 1 ? 's' : ''} for "${searchTerm}"`
                    : `No courses found for "${searchTerm}"`
                  }
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                {filteredCourses.length > 0 ? (
                  filteredCourses.map((course) => (
                    <CourseCard key={course.id} course={course} />
                  ))
                ) : (
                  <div className="col-span-full text-center py-12">
                    <p className="text-slate-600 text-lg">
                      {searchTerm ? `No courses match your search. Try different keywords.` : `No courses available yet. Please check back later!`}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AdminDashboard;
